import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {object} AddOnLicenseData
 * @property {number} addon_licenses - Number of addon licenses to request
 * @property {number} price_per_license - Price per individual license
 * @property {number} amount - Total amount (addon_licenses * price_per_license)
 * @property {"online"} source - Payment source for online payments
 * @property {string} currency - Currency of the amount (e.g., "INR", "USD")
 */

/**
 * @typedef {object} AddOnLicenseDataOffline
 * @property {number} addon_licenses - Number of addon licenses to request
 * @property {number} price_per_license - Price per individual license
 * @property {number} amount - Total amount (addon_licenses * price_per_license)
 * @property {"offline"} source - Payment source for offline payments
 * @property {string} method - Payment method (e.g., "RTGS", "NEFT", "UPI")
 * @property {string} reference_id - Payment reference ID
 * @property {File[]} payment_proofs - Array of payment proof files
 * @property {string} currency - Currency of the amount (e.g., "INR", "USD")
 */

/**
 * @typedef {object} PendingInvoice
 * @property {number} invoice_id - The ID of the existing unpaid invoice
 * @property {"unpaid_exists"} status - Status indicating an unpaid invoice exists
 */

/**
 * @typedef {object} AddOnInvoiceResponse
 * @property {number} invoice_id - The ID of the newly created addon invoice
 */

/**
 * @description Custom hook to request addon licenses
 */
export const useAddOnLicenses = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState(null);

  const { axiosInstance } = useAxios();

  /**
   * @description Request addon licenses
   * @async
   * @param {AddOnLicenseData | AddOnLicenseDataOffline} licenseData - The addon license request data
   * @param {() => void} [onSuccess] - Callback function to be called on success
   * @returns {Promise<{success: boolean, data: AddOnInvoiceResponse|PendingInvoice|null}>} Resolves with success flag and response data
   */
  const requestAddOnLicenses = async (licenseData, onSuccess) => {
    try {
      setLoading(true);
      setError("");
      setSuccess(false);
      setPendingInvoice(null);

      const isOnline = licenseData.source === "online";
      let response;

      if (isOnline) {
        // Online payment: send plain JSON body
        response = await axiosInstance.post(
          `${TENANT_DOMAIN}/billing/licenses/addons/request`,
          {
            addon_licenses: licenseData.addon_licenses,
            price_per_license: licenseData.price_per_license,
            amount: licenseData.amount,
            source: licenseData.source,
            currency: licenseData.currency,
          },
        );
      } else {
        // Offline payment: send FormData with files
        const formData = new FormData();
        formData.append("addon_licenses", licenseData.addon_licenses);
        formData.append("price_per_license", licenseData.price_per_license);
        formData.append("amount", licenseData.amount);
        formData.append("source", licenseData.source);
        formData.append("method", licenseData.method);
        formData.append("reference_id", licenseData.reference_id);
        formData.append("currency", licenseData.currency);

        // Append payment proof files
        if (
          licenseData.payment_proofs &&
          licenseData.payment_proofs.length > 0
        ) {
          licenseData.payment_proofs.forEach((file) => {
            formData.append("payment_proofs[]", file);
          });
        }

        response = await axiosInstance.post(
          `${TENANT_DOMAIN}/billing/licenses/addons/request`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );
      }

      const responseData = response?.data?.data || null;

      setSuccess(true);
      if (onSuccess) onSuccess(responseData);
      return { success: true, data: responseData };
    } catch (err) {
      setSuccess(false);

      const statusCode = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      const errorMessage = err?.response?.data?.message;
      const responseData = err?.response?.data?.data || null;

      // Handle 422: unpaid addon invoice already exists
      if (statusCode === 422 && responseData?.status === "unpaid_exists") {
        setError(errorMessage || "An unpaid addon invoice already exists.");
        setPendingInvoice({
          invoice_id: responseData.invoice_id,
          status: responseData.status,
        });
        return { success: false, data: responseData };
      }

      if (
        statusCode === 500 ||
        err?.status === 500 ||
        errorCode === "INTERNAL_SERVER_ERROR"
      ) {
        setError(errorMessage || ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        const apiErrors = err?.response?.data?.errors;
        const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

        setError(firstError || errorMessage || ERROR_MESSAGES.UNKNOWN);
      }
      return { success: false, data: null };
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, success, pendingInvoice, requestAddOnLicenses };
};

